<template>
  <div v-if="visible" class="login-warning" :class="warningClass">
    <div class="notification" :class="notificationClass">
      <!-- Close button for dismissible notifications -->
      <button
        v-if="showCloseButton"
        class="delete"
        @click="handleClose"
        aria-label="Close notification"
      ></button>

      <div class="warning-content">
        <!-- Failed attempts warning -->
        <div v-if="showFailedAttempts" class="warning-section">
          <div class="warning-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="warning-text">
            <p class="warning-title">Login Failed</p>
            <p class="warning-message">
              {{ failedAttemptsMessage }}
            </p>
          </div>
        </div>

        <!-- Account locked warning with countdown -->
        <div v-if="showAccountLocked" class="warning-section">
          <div class="warning-icon">
            <i class="fas fa-lock"></i>
          </div>
          <div class="warning-text">
            <p class="warning-title">Account Temporarily Locked</p>
            <p class="warning-message">
              Your account has been temporarily locked due to too many failed login attempts.
            </p>
            <div v-if="showCountdown" class="countdown-container">
              <p class="countdown-label">Time remaining until unlock:</p>
              <div class="countdown-display">
                <span class="countdown-time">{{ formattedCountdown }}</span>
              </div>
            </div>
            <p v-else-if="lockoutExpired" class="unlock-message">
              You can now attempt to log in again.
            </p>
            <p v-else class="lockout-status">
              Please wait for the lockout period to expire.
            </p>
          </div>
        </div>

        <!-- General error message -->
        <div v-if="showGeneralError" class="warning-section">
          <div class="warning-icon">
            <i class="fas fa-times-circle"></i>
          </div>
          <div class="warning-text">
            <p class="warning-title">Login Error</p>
            <p class="warning-message">
              {{ generalErrorMessage }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { countdownTimerMixin } from '@/composables/useCountdownTimer'

/**
 * LoginWarning Component
 *
 * Displays enhanced login warnings with support for:
 * - Failed login attempt tracking
 * - Account lockout with real-time countdown timer
 * - General error messages
 *
 * Props:
 * - error: Error object from authentication API
 * - visible: Boolean to control visibility
 *
 * Events:
 * - unlock: Emitted when countdown reaches zero
 * - close: Emitted when warning should be closed
 */
export default {
  name: 'LoginWarning',
  mixins: [countdownTimerMixin],
  props: {
    error: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      remainingSeconds: 0,
      formattedCountdown: '0 seconds',
      isUnlocked: false,
      lockoutExpired: false,
      validLockoutTime: false
    }
  },
  mounted() {
    // Handle initial error after component is fully mounted and mixin is initialized
    if (this.error) {
      this.handleErrorChange(this.error)
    }
  },
  computed: {
    /**
     * Determines the CSS class for the warning container
     */
    warningClass() {
      if (this.showAccountLocked) {
        return 'warning-locked'
      }
      if (this.showFailedAttempts) {
        return 'warning-attempts'
      }
      return 'warning-error'
    },

    /**
     * Determines the CSS class for the notification
     */
    notificationClass() {
      if (this.showAccountLocked) {
        return 'is-danger'
      }
      if (this.showFailedAttempts) {
        return 'is-warning'
      }
      return 'is-danger'
    },

    /**
     * Whether to show failed attempts warning
     */
    showFailedAttempts() {
      return this.error &&
             this.error.code === 401 &&
             this.error.loginAttempts
    },

    /**
     * Whether to show account locked warning
     */
    showAccountLocked() {
      return this.error &&
             this.error.code === 423 &&
             this.error.accountLocked
    },

    /**
     * Whether to show general error message
     */
    showGeneralError() {
      return this.error &&
             !this.showFailedAttempts &&
             !this.showAccountLocked
    },

    /**
     * Whether to show countdown timer
     */
    showCountdown() {
      return this.showAccountLocked &&
             this.validLockoutTime &&
             this.remainingSeconds > 0 &&
             !this.lockoutExpired
    },

    /**
     * Whether to show close button for dismissible notifications
     */
    showCloseButton() {
      // Show close button for all notification types including account locked
      // Users should be able to manually dismiss account lockout notifications
      return this.showFailedAttempts ||
             this.showGeneralError ||
             this.showAccountLocked
    },

    /**
     * Message for failed attempts
     */
    failedAttemptsMessage() {
      if (!this.error || !this.error.loginAttempts) {
        return ''
      }

      const { failed, remaining } = this.error.loginAttempts
      return `${failed} failed attempt(s). ${remaining} attempt(s) remaining before account lockout.`
    },

    /**
     * General error message
     */
    generalErrorMessage() {
      return this.error ? this.error.message : ''
    },

    // formattedCountdown is now managed by the data property and updated by the timer
  },
  watch: {
    /**
     * Watch for changes in error prop to start/stop countdown
     */
    error: {
      handler(newError) {
        this.handleErrorChange(newError)
      }
    },

    /**
     * Watch for visibility changes to clean up timer
     */
    visible(newVisible) {
      if (!newVisible) {
        this.stopCountdown()
      }
    }
  },
  methods: {
    /**
     * Handles error prop changes
     * @param {Object} newError - New error object
     */
    handleErrorChange(newError) {
      this.stopCountdown()
      this.resetLockoutState()

      if (newError && newError.accountLocked && newError.accountLocked.lockedUntil) {
        this.initializeAccountLockout(newError.accountLocked.lockedUntil, newError.accountLocked.unlockTimeRemaining)
      }
    },

    /**
     * Resets all lockout-related state
     */
    resetLockoutState() {
      this.isUnlocked = false
      this.lockoutExpired = false
      this.validLockoutTime = false
      this.remainingSeconds = 0
      this.formattedCountdown = '0 seconds'
    },

    /**
     * Validates and initializes account lockout
     * @param {string|number} lockedUntil - Timestamp when account will be unlocked
     * @param {string} unlockTimeRemaining - Server-provided remaining time (optional)
     */
    initializeAccountLockout(lockedUntil, unlockTimeRemaining = null) {
      // Try to use server-provided remaining time first if available
      // This is more reliable than timestamp calculation for UTC/timezone issues
      if (unlockTimeRemaining !== null && unlockTimeRemaining !== undefined) {
        const serverSeconds = unlockTimeRemaining
        if (serverSeconds > 0) {
          this.initializeFromServerTime(serverSeconds)
          return
        }
      }

      // Fallback to timestamp-based calculation with UTC compensation
      const lockoutTimestamp = this.validateLockoutTimestamp(lockedUntil)

      if (!lockoutTimestamp) {
        this.handleInvalidLockout()
        return
      }

      const now = Date.now()
      let remainingMs = lockoutTimestamp - now

      // Enhanced compensation for UTC timestamps that appear in the past
      // This is common when server sends UTC time and there are network delays
      const maxCompensationBuffer = 60000 // 60 seconds buffer for UTC/network issues

      if (remainingMs <= 0) {
        // If timestamp is in the past but within reasonable range, apply compensation
        if (remainingMs > -maxCompensationBuffer) {
          // For timestamps slightly in the past, give them a reasonable remaining time
          // This handles UTC/timezone sync issues and network delays
          remainingMs = Math.max(30000, maxCompensationBuffer + remainingMs) // At least 30 seconds
        } else {
          // Timestamp is too far in the past, treat as expired
          this.handleExpiredLockout()
          return
        }
      }

      // Lockout is valid and still active
      this.validLockoutTime = true

      // Set initial remaining seconds for immediate display
      this.remainingSeconds = Math.floor(remainingMs / 1000)
      this.formattedCountdown = this.countdownTimer ? this.countdownTimer.formatTime(this.remainingSeconds) : `${this.remainingSeconds} seconds`

      // Start countdown using the adjusted remaining time instead of original timestamp
      this.startAccountLockoutCountdown(now + remainingMs)
    },

    /**
     * Validates the lockout timestamp with enhanced timezone and network delay handling
     * @param {string|number} lockedUntil - Timestamp to validate
     * @returns {number|null} Valid timestamp in milliseconds or null if invalid
     */
    validateLockoutTimestamp(lockedUntil) {
      if (!lockedUntil) {
        return null
      }

      let timestamp

      // Handle different timestamp formats
      if (typeof lockedUntil === 'string') {
        // For string timestamps, ensure proper UTC parsing
        // The 'Z' suffix indicates UTC, which Date constructor handles correctly
        const dateObj = new Date(lockedUntil)

        // Validate the date object is valid
        if (isNaN(dateObj.getTime())) {
          return null
        }

        timestamp = dateObj.getTime()
      } else if (typeof lockedUntil === 'number') {
        // Handle numeric timestamps (could be seconds or milliseconds)
        // Timestamps after year 2001 in milliseconds are > 1e12
        timestamp = lockedUntil > 1e12 ? lockedUntil : lockedUntil * 1000
      } else {
        return null
      }

      // Validate that it's a valid timestamp
      if (isNaN(timestamp) || timestamp <= 0) {
        return null
      }

      // Enhanced validation: Allow timestamps slightly in the past to account for network delays
      // but reject timestamps that are too far in the future
      const now = Date.now()
      const maxPastDelay = 30000 // Allow up to 30 seconds in the past (network delay)
      const maxLockoutDuration = 24 * 60 * 60 * 1000 // 24 hours in future

      // Check if timestamp is too far in the past (beyond reasonable network delay)
      if (timestamp < now - maxPastDelay) {
        // Don't return null here - let the calling function handle with buffer logic
      }

      // Check if timestamp is too far in the future
      if (timestamp > now + maxLockoutDuration) {
        return null
      }

      return timestamp
    },

    /**
     * Handles invalid lockout timestamps
     */
    handleInvalidLockout() {
      this.validLockoutTime = false
      this.lockoutExpired = false
      // Keep the account locked state but don't show countdown
      // This prevents showing the unlock message for invalid data
    },

    /**
     * Handles lockout that has already expired
     */
    handleExpiredLockout() {
      this.validLockoutTime = false
      this.lockoutExpired = true
      this.remainingSeconds = 0
      this.formattedCountdown = '0 seconds'

      // Emit unlock event to parent component
      this.$emit('unlock')

      // Auto-close warning after a short delay
      setTimeout(() => {
        this.$emit('close')
      }, 3000)
    },

    /**
     * Starts the account lockout countdown timer
     * @param {string|number} lockedUntil - Timestamp when account will be unlocked
     */
    startAccountLockoutCountdown(lockedUntil) {
      if (!this.countdownTimer) {
        // Retry in the next tick to ensure the mixin has been fully initialized
        this.$nextTick(() => {
          if (this.countdownTimer) {
            this.startAccountLockoutCountdown(lockedUntil)
          }
        })
        return
      }

      this.startCountdown(lockedUntil, {
        onTick: (seconds, formatted) => {
          this.remainingSeconds = seconds
          this.formattedCountdown = formatted
        },
        onComplete: () => {
          this.handleUnlock()
        }
      })
    },

    /**
     * Handles when countdown reaches zero
     */
    handleUnlock() {
      this.lockoutExpired = true
      this.remainingSeconds = 0
      this.formattedCountdown = '0 seconds'

      // Emit unlock event to parent component
      this.$emit('unlock')

      // Auto-close warning after a short delay
      setTimeout(() => {
        this.$emit('close')
      }, 3000)
    },

    /**
     * Parses server-provided remaining time
     * @param {number|string} remainingTime - Remaining time in seconds (number) or time string
     * @returns {number} Total seconds or 0 if parsing fails
     */
    parseServerRemainingTime(remainingTime) {
      // If it's already a number (seconds), use it directly
      if (typeof remainingTime === 'number') {
        return Math.max(0, Math.floor(remainingTime))
      }

      // If it's a string, try to parse it
      if (typeof remainingTime === 'string') {
        // First try to parse as a number
        const numericValue = parseInt(remainingTime)
        if (!isNaN(numericValue) && numericValue >= 0) {
          return numericValue
        }

        // Fallback: try to parse time strings like "14 minutes 55 seconds"
        const patterns = [
          /(\d+)\s*minutes?\s*(\d+)\s*seconds?/i,  // "14 minutes 55 seconds"
          /(\d+)\s*minutes?/i,                      // "14 minutes"
          /(\d+)\s*seconds?/i                       // "55 seconds"
        ]

        for (const pattern of patterns) {
          const match = remainingTime.match(pattern)
          if (match) {
            if (pattern === patterns[0]) {
              // Minutes and seconds
              const minutes = parseInt(match[1]) || 0
              const seconds = parseInt(match[2]) || 0
              return (minutes * 60) + seconds
            } else if (pattern === patterns[1]) {
              // Only minutes
              const minutes = parseInt(match[1]) || 0
              return minutes * 60
            } else if (pattern === patterns[2]) {
              // Only seconds
              const seconds = parseInt(match[1]) || 0
              return seconds
            }
          }
        }
      }

      return 0
    },

    /**
     * Initializes lockout using server-provided remaining time
     * @param {number} remainingSeconds - Remaining seconds from server
     */
    initializeFromServerTime(remainingSeconds) {
      if (remainingSeconds <= 0) {
        this.handleExpiredLockout()
        return
      }

      // Set up countdown using server time
      this.validLockoutTime = true
      this.remainingSeconds = remainingSeconds
      this.formattedCountdown = remainingSeconds + ' seconds'

      // Start countdown using current time + server remaining time
      const targetTime = Date.now() + (remainingSeconds * 1000)
      this.startAccountLockoutCountdown(targetTime)
    },

    /**
     * Handles close button click
     */
    handleClose() {
      // Stop any running countdown timer when manually closing
      this.stopCountdown()
      this.resetLockoutState()

      // Emit close event to parent component
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.login-warning {
  margin-bottom: 1rem;

  .notification {
    padding: 1rem;
    border-radius: 6px;
    box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1), 0 0px 0 1px rgba(10, 10, 10, 0.02);

    &.is-warning {
      background-color: #fff3cd;
      border: 1px solid #ffeaa7;
      color: #856404;
    }

    &.is-danger {
      background-color: #f8d7da;
      border: 1px solid #f5c6cb;
      color: #721c24;
    }
  }

  .warning-content {
    display: flex;
    align-items: flex-start;
  }

  .warning-section {
    display: flex;
    align-items: flex-start;
    width: 100%;
  }

  .warning-icon {
    margin-right: 0.75rem;
    margin-top: 0.125rem;

    i {
      font-size: 1.25rem;
    }
  }

  .warning-text {
    flex: 1;
  }

  .warning-title {
    font-weight: 600;
    margin-bottom: 0.25rem;
    font-size: 1rem;
  }

  .warning-message {
    margin-bottom: 0.5rem;
    line-height: 1.4;
  }

  .countdown-container {
    margin-top: 0.75rem;
    padding: 0.75rem;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }

  .countdown-label {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .countdown-display {
    text-align: center;
  }

  .countdown-time {
    font-size: 1.125rem;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    padding: 0.5rem 1rem;
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    display: inline-block;
  }

  .unlock-message {
    margin-top: 0.75rem;
    padding: 0.5rem;
    background-color: rgba(72, 199, 116, 0.1);
    color: #155724;
    border-radius: 4px;
    font-weight: 500;
  }

  .lockout-status {
    margin-top: 0.75rem;
    padding: 0.5rem;
    background-color: rgba(255, 193, 7, 0.1);
    color: #856404;
    border-radius: 4px;
    font-weight: 500;
    font-style: italic;
  }
}

// Dark theme support using application variables
.dashboard.is-dark {
  .login-warning {
    .notification {
      &.is-warning {
        background-color: rgba(255, 221, 87, 0.1);
        border-color: rgba(255, 221, 87, 0.3);
        color: #ffdd57;
      }

      &.is-danger {
        background-color: rgba(255, 107, 122, 0.1);
        border-color: rgba(255, 107, 122, 0.3);
        color: #ff6b7a;
      }
    }

    .countdown-container {
      background-color: rgba(31, 33, 40, 0.3); // Using $x with opacity
      border: 1px solid rgba(221, 221, 223, 0.1); // Using $y with opacity
    }

    .countdown-time {
      background-color: rgba(31, 33, 40, 0.5); // Using $x with opacity
      color: #dddddf; // Using $y
      border: 1px solid rgba(221, 221, 223, 0.2);
    }

    .unlock-message {
      background-color: rgba(72, 199, 116, 0.1);
      color: #48c774;
      border: 1px solid rgba(72, 199, 116, 0.3);
    }

    .lockout-status {
      background-color: rgba(255, 221, 87, 0.1);
      color: #ffdd57;
      border: 1px solid rgba(255, 221, 87, 0.3);
    }

    .warning-icon i {
      color: inherit;
    }

    // Ensure delete button is visible in dark theme
    .delete {
      background-color: rgba(221, 221, 223, 0.1);
      border: 1px solid rgba(221, 221, 223, 0.3);

      &:hover {
        background-color: rgba(221, 221, 223, 0.2);
      }

      &:focus {
        box-shadow: 0 0 0 0.125em rgba(221, 221, 223, 0.25);
      }

      &::before,
      &::after {
        background-color: #dddddf;
      }
    }
  }
}
</style>
